import { useState } from "react"

import { Button, Flex, Tooltip, Typography } from "antd"
import cls from "classnames"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import ButtonComponent from "../../../../Components/Button"
import Private from "../../../../Components/Private"
import Icon from "../../../../Icon"
import Images from "../../../../Images"
import {
  dateFormat,
  formatCurrency,
  pinIconColor,
  serviceImg,
  serviceMap
} from "../../../../Utils/utils"
import AlertStatusTag from "../../../AlertsPage/components/AlertStatusTag/AlertStatusTag"
import CreditRequestStatus from "../CreditRequestStatus/CreditRequestStatus"

import "./CreditCard.less"

const { Text } = Typography

const CreditCard = ({ cr, handleSelectRow, handleChangeStatus }) => {
  const { theme } = useSelector((state) => state.nav)
  const { t } = useTranslation()

  const creditIdText = cr?.sequence_number < 10 ? `0${cr?.sequence_number}` : cr?.sequence_number

  const isRequiresProof = cr?.request_status === "Requires proof"

  return (
    <Flex vertical gap={16} className="alert-card">
      <Flex align="center" justify="space-between" gap={10}>
        <Flex gap={8} className="cr-card-header-tags">
          <div className="grey-block cr-sequence-number">
            <Text strong ellipsis>
              {creditIdText}
            </Text>
          </div>
          <CreditRequestStatus cr={cr} />
          <div className="grey-block">
            <img
              width={16}
              height={16}
              src={Images[serviceImg?.[theme]?.[cr?.alert_service]]}
              alt={cr?.alert_service}
            />
            <Text strong>{serviceMap[cr?.alert_service]}</Text>
          </div>
        </Flex>
        <Flex gap={4}>
          <PinComponent cr={cr} theme={theme} t={t} onPin={(cr) => handleSelectRow(cr, "pin")} />
        </Flex>
      </Flex>
      <div className="fields-list">
        <div className="data-block">
          <Text type="secondary" strong>
            Amount
          </Text>
          <CopyableValue
            value={<Private>{formatCurrency(cr?.amount)}</Private>}
            copyValue={formatCurrency(cr?.amount)}
            className="amount"
          />
        </div>
        <div className="data-block">
          <Text type="secondary" strong ellipsis>
            Transaction Date
          </Text>
          <Text strong ellipsis>
            {dayjs(cr?.transaction_timestamp).format(dateFormat)}
          </Text>
        </div>
        <div className="data-block">
          <Text type="secondary" strong ellipsis>
            Card
          </Text>
          <CardLastFour cr={cr} theme={theme} />
        </div>
        <div className="data-block">
          <Text type="secondary" strong ellipsis>
            Alert Date
          </Text>
          <Text strong ellipsis>
            {dayjs(cr?.alert_timestamp).format(dateFormat)}
          </Text>
        </div>
        <div className="data-block">
          <Text type="secondary" strong ellipsis>
            ARN
          </Text>
          {cr?.arn ? (
            <CopyableValue value={<Private>{cr?.arn}</Private>} copyValue={cr?.arn} />
          ) : (
            <Text strong>-</Text>
          )}
        </div>
        <div className="data-block">
          <Text type="secondary" strong ellipsis>
            Auth Code
          </Text>
          {cr?.auth_code ? (
            <CopyableValue value={<Private>{cr?.auth_code}</Private>} copyValue={cr?.auth_code} />
          ) : (
            <Text strong>-</Text>
          )}
        </div>
      </div>
      <Flex vertical gap={12}>
        <AlertStatusTag alert={cr} changeStatusDisabled isDemo={false} />
        <Flex gap={12}>
          <Button
            style={{ height: "36px", width: "36px" }}
            icon={<Icon type={"eye"} style={{ color: "#7A7A83" }} />}
            onClick={() => handleSelectRow(cr, "view_cr_details")}
          />
          {!isRequiresProof && (
            <ButtonComponent
              style={{ height: "36px", flex: 1 }}
              onClick={() => handleSelectRow(cr, "preview")}
            >
              View proof
            </ButtonComponent>
          )}
          {isRequiresProof && (
            <ButtonComponent
              type="primary"
              style={{ height: "36px", flex: 1 }}
              onClick={() => handleSelectRow(cr, "attach")}
            >
              Submit proof
            </ButtonComponent>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

const PinComponent = ({ cr, theme, t, onPin }) => {
  const pinned = cr?.pinned
  if (cr?.["request_status"]?.toLowerCase() === "requires proof") {
    return <Icon type={"clock_alert"} style={{ fontSize: "20px", color: "#D71313" }} />
  } else {
    return (
      <Tooltip title={t(pinned ? "Unpin" : "Pin")}>
        <button
          type="text"
          onClick={(e) => {
            e.stopPropagation()
            onPin(cr)
          }}
          className={cls("icon-btn", { active: pinned })}
        >
          <Icon color={pinIconColor[theme]} type={pinned ? "pin_active" : "pin"} />
        </button>
      </Tooltip>
    )
  }
}

const CopyableValue = ({ value, copyValue = value, className }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = (e) => {
    e.stopPropagation()
    navigator.clipboard.writeText(copyValue)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="copy-row">
      <Text strong className={cls("copy-row-text", className)} title={copyValue}>
        {value}
      </Text>
      <button type="button" className="copy-row-btn" onClick={handleCopy}>
        {copied ? <Icon type={"check"} style={{ color: "#52c41a" }} /> : <Icon type={"copy"} />}
      </button>
    </div>
  )
}

const CardLastFour = ({ cr, theme }) => {
  const _ = cr?.card_last4
  return (
    <Flex align="center" gap={8} style={{ minWidth: 0 }}>
      {cr?.brand && (
        <img
          src={Images[`${cr?.brand?.toLowerCase()}${theme === "dark" ? "_dark" : ""}`]}
          width={20}
          height={20}
          alt={cr?.brand}
          style={{ flexShrink: 0 }}
        />
      )}
      {_ && cr?.card_bin ? (
        <CopyableValue
          value={<Private mask="••••">{cr?.card_bin + " ••• " + _}</Private>}
          copyValue={cr?.card_last4}
        />
      ) : (
        _ && <CopyableValue value={<Private mask="••••">{`•••• ${_}`}</Private>} copyValue={_} />
      )}
    </Flex>
  )
}

export default CreditCard
